import React from "react";
import { Badge, Typography } from "@mui/material";
import {
  BodypartType,
  ExerciseAssetDto,
  IUpdateAdminPublicExerciseAssetCommand,
  IUpdateExerciseAssetCommand,
  MovementPatternType,
  PaginatedListOfExerciseAssetDto,
} from "@growth-machine-llc/stridist-api-client";
import { CategoryTypeLabel } from "./ManageLibraryExercise";
import { AddedLozenge } from "../item/AddedLozenge";
import { ExerciseCategoryMultiSelect } from "./ExerciseCategoryMultiSelect";
import clsx from "clsx";
import { exerciseLibraryStyles } from "./exerciseLibraryStyles";
import { UseMutateFunction } from "@tanstack/react-query";
import { ExerciseSelectedCategory } from "./ExerciseSelectedCategory";

export type UpdateExerciseFunctionType = UseMutateFunction<
  void,
  unknown,
  IUpdateExerciseAssetCommand | IUpdateAdminPublicExerciseAssetCommand,
  PaginatedListOfExerciseAssetDto
>;

interface ExerciseCategoryBadgeProps {
  label: CategoryTypeLabel;
  selectedItems: (BodypartType | MovementPatternType)[];
  labelMap: Record<string, string>;
  open: boolean;
  setOpen: (val: boolean) => void;
  anchorEl: HTMLElement | null;
  setAnchorEl: (el: HTMLElement | null) => void;
  exercise: ExerciseAssetDto;
  showDashWhenEmpty?: boolean;
  classes?: Partial<Record<"badge" | "category", string>>;
  readonly?: boolean;
  updateExercise?: UpdateExerciseFunctionType;
  showOnlySelected?: boolean;
}

export const ExerciseCategoryBadge: React.FC<ExerciseCategoryBadgeProps> = ({
  label,
  selectedItems,
  labelMap,
  open,
  setOpen,
  anchorEl,
  setAnchorEl,
  exercise,
  showDashWhenEmpty = true,
  classes,
  readonly = false,
  showOnlySelected = false,
  updateExercise,
}) => {
  const s = exerciseLibraryStyles();
  const first = selectedItems && selectedItems[0];

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setOpen(true);
    setAnchorEl(e.currentTarget);
    e.stopPropagation();
    e.preventDefault();
  };

  const initialSelected = React.useMemo(() => {
    return selectedItems || [];
  }, [selectedItems]);

  return (
    <>
      {first ? (
        <Badge
          badgeContent={
            selectedItems.length > 1 ? `+${selectedItems.length - 1}` : null
          }
          invisible={selectedItems.length <= 1}
          className={classes?.badge}
          color="primary"
        >
          <AddedLozenge
            sx={{ "&.MuiBox-root": { m: 0 }, width: "fit-content" }}
            className={clsx(s.badgeCategoryTag, classes?.category)}
            text={labelMap[first]}
            onClick={handleClick}
          />
        </Badge>
      ) : showDashWhenEmpty ? (
        <Typography
          onClick={handleClick}
          sx={{
            cursor: readonly ? "default" : "pointer",
            paddingY: 2,
          }}
        >
          -
        </Typography>
      ) : null}

      {showOnlySelected ? (
        <ExerciseSelectedCategory
          selectedOptions={initialSelected}
          label={label}
          anchorEl={anchorEl}
          open={open}
          setOpen={setOpen}
          onClose={() => {
            setOpen(false);
            setAnchorEl(null);
          }}
        />
      ) : (
        <ExerciseCategoryMultiSelect
          label={label}
          open={open}
          setOpen={setOpen}
          anchorEl={anchorEl}
          exercise={exercise}
          onClose={() => {
            setOpen(false);
            setAnchorEl(null);
          }}
          initialSelected={initialSelected}
          showButton={false}
          readonly={readonly}
          updateExercise={updateExercise}
        />
      )}
    </>
  );
};
